import axios from 'axios';
import { log } from '../utils/logger';

// Detectar URL base automaticamente em produção
const getBaseURL = () => {
  // Se REACT_APP_API_URL estiver definido, usar
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Em produção, usar a mesma origem (mesmo domínio)
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
    return '/api';
  }
  
  // Em desenvolvimento, usar localhost
  return 'http://localhost:5000/api';
};

const baseURL = getBaseURL();

const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1200;

function isRetriableError(error) {
  const status = error.response?.status;
  const is5xx = status >= 500 && status < 600;
  const is502 = status === 502;
  const isNetwork = error.code === 'ERR_NETWORK' || error.message?.includes('Network');
  return is502 || is5xx || isNetwork;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  try {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
      }
    }
  } catch (error) {
    log.warn('Erro ao ler cookie:', error);
  }
  return null;
};

// Interceptor para adicionar CSRF token em requisições de escrita
// Ao enviar FormData, NÃO definir Content-Type - o browser deve setar
// "multipart/form-data; boundary=..." automaticamente. Definir só
// "multipart/form-data" quebra o parse no servidor (falta o boundary).
api.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    const method = (config.method || 'get').toUpperCase();
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      // Tentar ler CSRF token do cookie
      let csrfToken = getCookieValue('csrf_token');
      
      // Se não encontrou, tentar ler de todas as formas possíveis
      if (!csrfToken && typeof document !== 'undefined') {
        // Tentar ler diretamente do document.cookie com diferentes variações
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const trimmed = cookie.trim();
          const [name, value] = trimmed.split('=');
          if (name === 'csrf_token' && value) {
            try {
              csrfToken = decodeURIComponent(value);
              break;
            } catch (e) {
              // Se falhar decode, tentar usar o valor direto
              csrfToken = value;
              break;
            }
          }
        }
      }
      
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      } else {
        // Se não encontrou CSRF token, não enviar header
        // O servidor é tolerante e permite requisições sem CSRF token na primeira vez
        // Ele vai gerar um novo token e definir no cookie
        if (process.env.NODE_ENV === 'development') {
          log.warn('⚠️ CSRF token não encontrado no cookie para requisição:', config.method, config.url);
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros e fazer retry em falhas temporárias (502, 5xx, rede)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};

    // Se for erro 403 de CSRF, apenas logar e rejeitar (sem retry)
    if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_TOKEN_INVALID') {
      log.error('❌ Erro CSRF - Token inválido. Verifique se está logado corretamente.');
      return Promise.reject(error);
    }

    // Se for erro 401, redirecionar para login apenas se não estiver em página pública
    if (error.response?.status === 401) {
      const requestUrl = config.url || '';
      const isVerifyRequest = requestUrl.includes('/auth/verify');
      if (isVerifyRequest) return Promise.reject(error);
      const currentPath = window.location.pathname;
      const isPublicPath = currentPath === '/admin' || currentPath === '/cadastro' ||
        currentPath === '/verify-email' || currentPath === '/esqueci-senha' ||
        currentPath === '/redefinir-senha' || currentPath === '/confirm-email-change';
      if (!isPublicPath) {
        window.location.href = '/admin';
      }
      return Promise.reject(error);
    }

    // Retry automático em falhas temporárias (502, 5xx, erro de rede)
    const retryCount = config.__retryCount ?? 0;
    if (isRetriableError(error) && retryCount < MAX_RETRIES) {
      config.__retryCount = retryCount + 1;
      await delay(RETRY_DELAY_MS);
      return api.request(config);
    }

    return Promise.reject(error);
  }
);

export default api;

